import type { IPagingFilter } from "@frui.ts/data";
import { bound } from "@frui.ts/helpers";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import TransferType from "entities/transferType";
import TransferRefundForRefundOfRefundPaymentTemplate from "entities/transferRefundForRefundOfRefundPaymentTemplate";
import { computed, reaction } from "mobx";
import type RefundForRefundOfRefundsRepository from "repositories/refundForRefundOfRefundsRepository";
import type EventBus from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type TransferRefundForRefundOfRefundsDetailViewModel from "./transferRefundForRefundOfRefundsDetailViewModel";
import type RefundsApprovalService from "services/refundsApprovalService";
import RefundForRefundOfRefundsListViewModel from "../refundForRefundOfRefundsListViewModel";
import { transferRefundForRefundOfRefundPaymentTypeChanged } from "services/eventBus";
import RefundType from "entities/refundType";
import RefundApprovalType from "entities/refundApprovalType";
import type RefundForRefundOfRefundResponse from "entities/refundForRefundOfRefundResponse";
import TransferPaymentType from "entities/transferPaymentType";
import RefundsForRefundsOfRefundsRequest, {
  RefundsForRefundsOfRefundsRequestValidationRules,
} from "entities/refundsForRefundsOfRefundsRequest";
import { CounterpartyBankAccountRequestValidationRules } from "entities/counterpartyBankAccountRequest";
import { emptyValue } from "utils/emptyValue";

export default abstract class TransferRefundForRefundOfRefundsViewModel extends RefundForRefundOfRefundsListViewModel<
  RefundForRefundOfRefundResponse,
  ScreenBase,
  RefundsForRefundsOfRefundsRequest
> {
  readonly modelName = "refundForRefundOfRefund";
  readonly transferType: TransferType;
  readonly approvalType = RefundApprovalType;

  constructor(
    protected detailViewModelFactory: (
      refundId: string,
      pagingFilter: IPagingFilter,
      refundType: TransferType
    ) => TransferRefundForRefundOfRefundsDetailViewModel,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected approvalService: RefundsApprovalService,
    protected optionsService: OptionsService,
    protected repository: RefundForRefundOfRefundsRepository,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
  }

  @computed
  get transferRefundPaymentTemplatesOptions() {
    return this.optionsService.entityOptions(TransferRefundForRefundOfRefundPaymentTemplate);
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.senderBankAccount?.accountNumber,
          this.filter.senderBankAccount?.bank,
          this.filter.countryId,
          this.filter.licensePlate,
          this.filter.paidAmount,
          this.filter.postingDate,
          this.filter.refundAmount,
          this.filter.refundPaymentTemplateId,
          this.filter.variableSymbol,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      ),
      // We need to validate bank account separately as it's nested object
      reaction(
        () => [this.filter.senderBankAccount?.accountNumber, this.filter.senderBankAccount?.bank],
        () => {
          this.validateBankAccountFilter();
        }
      ),
      this.eventBus.subscribe(
        transferRefundForRefundOfRefundPaymentTypeChanged,
        this.onTransferRefundForRefundOfRefundPaymentTypeChanged
      )
    );
    super.onInitialize();
  }

  @bound
  validateBankAccountFilter() {
    attachAutomaticValidator(this.filter.senderBankAccount, CounterpartyBankAccountRequestValidationRules);
    validate(this.filter.senderBankAccount);
  }

  @bound
  onItemClick() {
    return (item: RefundForRefundOfRefundResponse) =>
      this.tryActivateChild(this.detailViewModelFactory(item.id, this.pagingFilter, this.transferType));
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;

    // TODO: Consider keeping as TransferPaymentType
    const filter = { ...this.appliedFilter };
    switch (this.transferType) {
      case TransferType.Domestic:
        filter.transferType = TransferPaymentType.Domestic;
        break;
      case TransferType.Sepa:
        filter.transferType = TransferPaymentType.Sepa;
        break;
      case TransferType.Foreign:
        filter.transferType = TransferPaymentType.Foreign;
        break;
    }

    this.repository
      .search({ limit: 1, offset: 0 }, { transferType: filter.transferType, approvedBy: RefundApprovalType.ChiefAccountant })
      .then(response => {
        if (response.items.length > 0) {
          this.isAnyExportable = true;
        }
      });

    return this.repository.search({ limit, offset }, filter);
  }

  protected createFilter() {
    return attachAutomaticValidator(new RefundsForRefundsOfRefundsRequest(), RefundsForRefundsOfRefundsRequestValidationRules);
  }

  @bound
  private onTransferRefundForRefundOfRefundPaymentTypeChanged(
    data: ReturnType<typeof transferRefundForRefundOfRefundPaymentTypeChanged>
  ) {
    const { originalTransferType } = data.payload;

    if (
      (originalTransferType === TransferPaymentType.Domestic && this.refundType === RefundType.DomesticTransfer) ||
      (originalTransferType === TransferPaymentType.Sepa && this.refundType === RefundType.SepaTransfer)
    ) {
      this.loadData();
    }
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService
        .entityOptions(TransferRefundForRefundOfRefundPaymentTemplate)
        .find(option => option.id === refundPaymentTemplateId)?.label ?? emptyValue
    );
  }

  async findNavigationChild(navigationName?: string) {
    if (navigationName && this.allowWrite) {
      return this.detailViewModelFactory(navigationName, this.pagingFilter, this.transferType);
    }

    return Promise.resolve(undefined);
  }
}
